import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/cargofl-ui';
import { visibleNav, type NavGroup } from '@/lib/nav';
import type { Session } from '@/lib/session';

/**
 * The rail. A dark, teal-tinted surface in both themes - it takes the sidebar
 * tokens, not the body palette, so it does not flip with the theme toggle.
 *
 * Groups collapse. A group containing the current route starts open, so arriving by
 * a deep link or a redirect never leaves somebody looking at a closed menu with no
 * idea where they are.
 */
export function AppSidebar({ session }: { session: Session }) {
  const groups = visibleNav(session.role);

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center px-2 py-1.5 group-data-[state=closed]/sidebar:justify-center group-data-[state=closed]/sidebar:px-0">
          {/* The on-dark wordmark: the same artwork with the lettering whitened, so
              the blue mark survives. A brightness(0)/invert filter over the normal
              file would flatten it to a silhouette.

              On the icon rail the wordmark shrank to an unreadable sliver, and there
              is no separate mark file, so the rail crops the same image to its mark:
              at 22px high the mark spans roughly x 7-27px of the artwork. */}
          <span className="block group-data-[state=closed]/sidebar:w-5 group-data-[state=closed]/sidebar:overflow-hidden">
            <img
              src="/logo-wordmark.svg"
              alt="CargoFL Timesheet"
              draggable={false}
              className="h-[22px] w-auto max-w-none object-contain object-left brightness-0 invert group-data-[state=closed]/sidebar:-ml-[7px]"
            />
          </span>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {groups.map((group) =>
                group.items.length === 0 ? (
                  <DirectLink key={group.title} group={group} />
                ) : (
                  <GroupMenu key={group.title} group={group} />
                ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <div className="px-2 py-1.5 text-[11px] leading-relaxed text-sidebar-foreground/60">
          Signed in as
          <div className="truncate font-medium text-sidebar-foreground">
            {session.alias ?? session.displayName}
          </div>
          <div className="truncate lowercase">{session.role}</div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

/** How an icon moves, once, as its page becomes current or its group opens (keyframes in styles/globals.css, `.nav-icon`); anything unlisted pops. */
const NAV_MOTION: Record<string, 'ring' | 'spin' | 'drop' | 'pop'> = {
  '/timesheet/calendar': 'ring',
  '/absence/calendar': 'ring',
  '/approvals': 'ring',
  '/admin/system': 'spin',
  '/timesheet/export': 'drop',
  '/import': 'drop',
};
const motionFor = (href?: string) => (href && NAV_MOTION[href]) || 'pop';

/** A heading that is itself the destination - Dashboard, Approvals, Reporting. */
function DirectLink({ group }: { group: NavGroup }) {
  return (
    <SidebarMenuItem>
      <NavLink to={group.href!} end={group.href === '/'} title={group.title}>
        {({ isActive }) => (
          <SidebarMenuButton asChild isActive={isActive}>
            <span>
              <group.icon className="nav-icon size-4" data-motion={motionFor(group.href)} />
              <span>{group.title}</span>
            </span>
          </SidebarMenuButton>
        )}
      </NavLink>
    </SidebarMenuItem>
  );
}

function GroupMenu({ group }: { group: NavGroup }) {
  const { pathname } = useLocation();
  // Open when the current route is inside it. Computed from the path rather than
  // held in state, so a redirect into a collapsed group still reveals where you are.
  const containsCurrent = group.items.some(
    (item) => pathname === item.href || pathname.startsWith(`${item.href}/`),
  );
  const [open, setOpen] = React.useState(containsCurrent);
  React.useEffect(() => {
    if (containsCurrent) setOpen(true);
  }, [containsCurrent]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton isActive={containsCurrent && !open}>
            <group.icon className="nav-icon size-4" data-motion="pop" />
            <span>{group.title}</span>
            {/* Not on the icon rail: there it sat beside the icon, pushed every
                group heading off the centre line the other rows sit on, and the
                rail is too narrow for it to say anything the tint does not. */}
            <ChevronRight
              className={`ml-auto size-4 shrink-0 transition-transform duration-200 group-data-[state=closed]/sidebar:hidden ${
                open ? 'rotate-90' : ''
              }`}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>

        <CollapsibleContent>
          {/* Indented and rule-led, so a child reads as belonging to the heading
              above it rather than as another top-level entry.

              On the icon rail there is no room to indent - it pushed the children's
              icons a column to the right of everything else - so the group is held
              together by a tinted band instead, centred on the same line. */}
          <SidebarMenu className="ml-3 border-l border-sidebar-border pl-2 group-data-[state=closed]/sidebar:mx-1 group-data-[state=closed]/sidebar:my-1 group-data-[state=closed]/sidebar:rounded-md group-data-[state=closed]/sidebar:border-l-0 group-data-[state=closed]/sidebar:bg-sidebar-accent/40 group-data-[state=closed]/sidebar:pl-0">
            {group.items.map((item) => (
              <SidebarMenuItem key={item.href}>
                {/* `end` matters: without it "/timesheet" prefix-matches
                    "/timesheet/week" and both rows light up as current. */}
                <NavLink to={item.href} end title={item.description}>
                  {({ isActive }) => (
                    <SidebarMenuButton asChild isActive={isActive}>
                      <span>
                        <item.icon className="nav-icon size-4" data-motion={motionFor(item.href)} />
                        <span>{item.title}</span>
                        {/* Marked rather than hidden: the menu says what exists and
                            what is still to come, instead of quietly omitting it. */}
                        {item.pending && (
                          <Badge
                            variant="outline"
                            className="ml-auto border-sidebar-border px-1 py-0 text-[9px] text-sidebar-foreground/60"
                          >
                            soon
                          </Badge>
                        )}
                      </span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
